import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtEur, fmtDate, STATE_LABELS } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import VersionBadge from "@/components/VersionBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, FileText, Filter, Edit, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function OffersPage() {
  const [params, setParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [stato, setStato] = useState(params.get("stato") || "all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (stato !== "all") p.stato = stato;
    try {
      const { data } = await api.get("/offers", { params: p });
      setOffers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [stato]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const canEdit = (o) => ["bozza", "da_correggere", "approvata", "inviata"].includes(o.stato);
  const canDelete = (o) => o.stato === "bozza";

  const handleEdit = (e, o) => {
    e.stopPropagation();
    navigate(`/offerte/${o.id}/modifica`);
  };

  const handleDelete = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare la bozza ${o.numero}?`)) return;
    try {
      await api.delete(`/offers/${o.id}`);
      toast.success("Offerta eliminata");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Archivio</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F2C59]">Offerte</h1>
        </div>
        <Button onClick={() => navigate("/offerte/nuova")} className="bg-[#0F2C59] hover:bg-[#173F7A]" data-testid="new-offer-page-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuova offerta
        </Button>
      </div>

      <Card className="p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca per numero…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="offer-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger className="w-52" data-testid="filter-stato"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATE_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 border-b border-slate-300">
                <th className="text-left px-5 py-3 font-semibold">Numero</th>
                <th className="text-left px-5 py-3 font-semibold">Cliente</th>
                <th className="text-left px-5 py-3 font-semibold">Creato da</th>
                <th className="text-left px-5 py-3 font-semibold">Stato</th>
                <th className="text-right px-5 py-3 font-semibold">Totale</th>
                <th className="text-left px-5 py-3 font-semibold">Data</th>
                <th className="text-right px-5 py-3 font-semibold w-32">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Caricamento…</td></tr>}
              {!loading && offers.length === 0 && (
                <tr><td colSpan="7" className="text-center py-14 text-slate-400">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  Nessuna offerta trovata
                </td></tr>
              )}
              {offers.map((o) => (
                <tr key={o.id}
                  onClick={() => navigate(`/offerte/${o.id}`)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`offer-row-${o.numero}`}
                >
                  <td className="px-5 py-3 font-mono font-medium text-[#0F2C59]">
                    <span className="inline-flex items-center gap-1.5">
                      {o.numero}
                      <VersionBadge version={o.versione} />
                    </span>
                  </td>
                  <td className="px-5 py-3">{o.cliente_nome || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{o.created_by_name || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge stato={o.stato} /></td>
                  <td className="px-5 py-3 text-right font-mono">{fmtEur(o.totale)}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/offerte/${o.id}`)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#0F2C59]"
                        title="Visualizza"
                        data-testid={`view-offer-${o.numero}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {canEdit(o) && (
                        <button
                          onClick={(e) => handleEdit(e, o)}
                          className="p-1.5 rounded hover:bg-blue-50 text-[#0F2C59] hover:text-[#173F7A]"
                          title="Modifica offerta"
                          data-testid={`edit-offer-${o.numero}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete(o) && (
                        <button
                          onClick={(e) => handleDelete(e, o)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500 hover:text-red-700"
                          title="Elimina bozza"
                          data-testid={`delete-offer-${o.numero}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
